import { useState, useCallback } from "react"
import { useTranslation } from "react-i18next"
import { Stack } from "@mui/material"

import { useUserOTPVerifyMutation } from "features/api"
import { snackbarMutation, buildGetErrorMessage } from "helpers/utils/mutations"
import Spinner from "widgets/common/Spinner"
import OTPCodeField from "widgets/common/otp/OTPCodeField"

interface OTPVerifierProps {
  username: string
  accessToken?: string
  onComplete: () => Promise<void>
}

const OTPVerifier = ({ username, accessToken, onComplete }: OTPVerifierProps) => {
  const { t } = useTranslation()
  const [otp, setOtp] = useState("")
  const [isCompleting, setIsCompleting] = useState<boolean>(false)

  const handleChange = (value: string) => {
    setOtp(value)
  }

  const [otpVerify, { isLoading: isVerifying }] = useUserOTPVerifyMutation()

  const otpVerifyWithSnackbar = useCallback(
    async (value: string) => {
      const req = { username, token: value, accessToken }
      const mutation = otpVerify(req).unwrap()
      return snackbarMutation({
        mutation,
        getErrorMessage: buildGetErrorMessage(
          t("error.ENABLING_ITEM", {
            item: t("account_settings.TWO_FA").toLowerCase(),
            count: 1,
            context: "female",
          }),
        ),
        getSuccessMessage: () =>
          t("success.ENABLING_ITEM", {
            item: t("account_settings.TWO_FA"),
            count: 1,
            context: "female",
          }),
      })
    },
    [username, accessToken, otpVerify, t],
  )
  const handleComplete = async (value: string) => {
    setIsCompleting(true)
    try {
      await otpVerifyWithSnackbar(value)
      handleChange("")
      await onComplete()
    } catch {
      handleChange("")
    }
    setIsCompleting(false)
  }

  return (
    <>
      <Stack direction="row" justifyContent={"center"} alignItems={"center"} gap={4}>
        {isVerifying || isCompleting ? (
          <Spinner />
        ) : (
          <OTPCodeField value={otp} onChange={handleChange} onComplete={handleComplete} />
        )}
      </Stack>
    </>
  )
}

export default OTPVerifier
